import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  HStack,
  Stack,
  Button,
  useDisclosure,
  Spinner,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { SortableContext } from '@dnd-kit/sortable';
import { tasksAPI, projectsAPI } from '../services/api';
import { Task, Project } from '../types';
import TaskCard from './TaskCard';
import SortableColumn from './SortableColumn';
import TaskModal from './TaskModal';
import ProjectStageDialogs from './ProjectStageDialogs';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { hasUnresolvedDependsOn } from '../utils/taskDepends';
import { sortTasksByPriority } from '../utils/taskSort';
import { useProjectStages } from '../hooks/useProjectStages';
import { useKanbanColumnWidths } from '../hooks/useKanbanColumnWidths';

interface KanbanBoardProps {
  projectId: string;
  project: Project;
  onProjectUpdate?: () => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ projectId, project, onProjectUpdate }) => {
  const { user } = useAuth();
  const stages = useProjectStages(projectId, project, user, onProjectUpdate);
  const { columns, setColumns, canManageStages } = stages;
  const columnWidths = useKanbanColumnWidths(projectId);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { open, onOpen, onClose } = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const addStageLabel = useBreakpointValue({ base: 'Add Stage', md: 'Add Stage' });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadTasks = useCallback(async () => {
    try {
      const response = await tasksAPI.getByProject(projectId);
      setTasks(response.data);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to load tasks',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Column reorder: active and over are column ids
    const isColumnDrag = columns.some((c) => c.id === activeId);
    if (isColumnDrag && !canManageStages) return;
    if (isColumnDrag && columns.some((c) => c.id === overId) && activeId !== overId) {
      const oldIndex = columns.findIndex((c) => c.id === activeId);
      const newIndex = columns.findIndex((c) => c.id === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        const prevColumns = [...columns];
        const reordered = arrayMove(columns, oldIndex, newIndex);
        setColumns(reordered.map((c, i) => ({ ...c, order: i })));
        try {
          await projectsAPI.reorderColumns(projectId, reordered.map((c) => c.id));
          if (onProjectUpdate) onProjectUpdate();
        } catch (error: any) {
          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Could not reorder columns',
            status: 'error',
          });
          setColumns(prevColumns);
        }
      }
      return;
    }

    // Task move
    const activeTask = tasks.find((t) => t._id === activeId);
    if (!activeTask) return;

    const canEditActiveTask = (() => {
      const isAdmin = user?.role === 'admin';
      const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
      const userId = user?.id;
      if (isAdmin || isMasterAdmin) return true;
      if (!userId) return false;
      const cb = activeTask.createdBy as any;
      const creatorId = cb && (cb._id || cb.id)?.toString?.();
      if (creatorId && creatorId === userId) return true;
      const participantIds: string[] = [];
      if (Array.isArray(activeTask.participants)) {
        participantIds.push(
          ...activeTask.participants
            .map((p: any) => (p?._id || p?.id || p)?.toString?.())
            .filter(Boolean) as string[]
        );
      }
      if (activeTask.assignedTo) {
        const aId = ((activeTask.assignedTo as any)._id || (activeTask.assignedTo as any).id)?.toString();
        if (aId) participantIds.push(aId);
      }
      return participantIds.includes(userId);
    })();
    if (!canEditActiveTask) return;

    let targetColumnId = columns.find((col) => col.id === overId)?.id;
    if (!targetColumnId) {
      const overTask = tasks.find((t) => t._id === overId);
      if (overTask) targetColumnId = overTask.status;
    }
    if (targetColumnId && targetColumnId !== activeTask.status) {
      if (
        hasUnresolvedDependsOn(activeTask, tasks, project.finishedColumnId, columns)
      ) {
        toast({
          title: 'Cannot change status',
          description:
            'Finish all tasks this one depends on before moving it to another column.',
          status: 'error',
        });
        return;
      }
      try {
        const newTasks = tasks.map((task) =>
          task._id === activeId ? { ...task, status: targetColumnId as any } : task
        );
        setTasks(newTasks);

        await tasksAPI.updateStatus(activeId, targetColumnId, activeTask.order);
        await loadTasks();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to update task',
          status: 'error',
        });
        await loadTasks();
      }
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    onOpen();
  };

  const handleTaskSaved = () => {
    // Samo osveži zadatke; modal se zatvara eksplicitno (Zatvori, kreiranje ili brisanje)
    setSelectedTask(null);
    loadTasks();
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    onOpen();
  };

  const getTasksByStatus = (status: string) => {
    return sortTasksByPriority(tasks.filter((task) => task.status === status));
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box width="100%">
      <HStack justifyContent="space-between" mb={8}>
        <Heading size="xl" color="gray.800">Kanban Board</Heading>
        <HStack gap={3}>
          {canManageStages && (
          <Button 
            variant="outline"
            onClick={stages.onStageDialogOpen}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'md',
            }}
          >
            <HStack gap={2}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{addStageLabel}</span>
            </HStack>
          </Button>
          )}
          <Button 
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            color="white"
            onClick={handleCreateTask}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'xl',
            }}
            fontWeight="semibold"
          >
            <HStack gap={2}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Task</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box
          width="100%"
          overflowX={{ base: 'visible', md: 'auto' }}
          overflowY={{ base: 'visible', md: 'visible' }}
          pb={4}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
          }}
          className="kanban-scroll"
        >
          <SortableContext items={columns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            align="flex-start"
            gap={6}
            minW={{ base: '100%', md: 'min-content' }}
          >
          {columns.map((column) => {
            const columnTasks = getTasksByStatus(column.id);
            return (
              <SortableColumn
                key={column.id}
                column={column}
                tasks={columnTasks}
                width={columnWidths.getWidth(column.id)}
                onWidthChange={(w) => columnWidths.setWidth(column.id, w)}
                onWidthReset={() => columnWidths.resetWidth(column.id)}
                onTaskClick={handleTaskClick}
                onDelete={canManageStages ? stages.handleDeleteStageClick : undefined}
                isFinished={project.finishedColumnId === column.id}
                onSetAsFinished={canManageStages && !project.finishedColumnId ? stages.handleSetAsFinished : undefined}
                canReorderColumns={canManageStages}
              />
            );
          })}
          </Stack>
          </SortableContext>
        </Box>
        <DragOverlay>
          {activeId ? (() => {
            const activeTask = tasks.find((t) => t._id === activeId);
            const statusLabel = activeTask ? columns.find((c) => c.id === activeTask.status)?.title : undefined;
            return activeTask ? (
              <Box opacity={0.9} transform="rotate(5deg)">
                <TaskCard task={activeTask} statusLabel={statusLabel} onClick={() => {}} />
              </Box>
            ) : null;
          })() : null}
        </DragOverlay>
      </DndContext>

      <TaskModal
        open={open}
        onClose={onClose}
        projectId={projectId}
        project={project}
        task={selectedTask}
        onSaved={handleTaskSaved}
      />

      <ProjectStageDialogs
        project={project}
        stageDialogOpen={stages.stageDialogOpen}
        closeStageDialog={stages.closeStageDialog}
        deleteDialogOpen={stages.deleteDialogOpen}
        onDeleteDialogClose={stages.onDeleteDialogClose}
        newStageTitle={stages.newStageTitle}
        setNewStageTitle={stages.setNewStageTitle}
        newStageColor={stages.newStageColor}
        setNewStageColor={stages.setNewStageColor}
        newStageSetAsFinished={stages.newStageSetAsFinished}
        setNewStageSetAsFinished={stages.setNewStageSetAsFinished}
        handleAddStage={stages.handleAddStage}
        confirmDeleteStage={stages.confirmDeleteStage}
        addButtonLabel={addStageLabel}
      />
    </Box>
  );
};

export default KanbanBoard;
